import type { CouponSettings } from '../../../../schema/sessionstore/entry.js';
import {
  BadRequestError,
  type IDefinitionService,
  type ISessionStoreRepository,
  type IUpdateCouponSettingsUseCase,
} from '../../../domain/entry.js';

/**
 * Use case for updating coupon settings.
 *
 * The repository handles correction synchronization. MY_BETS updates sync to WagerSettings,
 * while PLAY and RESULT manage their own correction state independently.
 *
 * @param couponSettingsRepository Coupon settings repository dependency
 * @param definitionService Definition service dependency
 * @returns Use case executor for updating coupon settings
 */
export function updateCouponSettingsUseCase(
  couponSettingsRepository: ISessionStoreRepository,
  definitionService: IDefinitionService
): IUpdateCouponSettingsUseCase {
  return {
    execute: async ({ product, payload, settingsKey }): ReturnType<IUpdateCouponSettingsUseCase['execute']> => {
      // TODO: check to not eslint disable, works in trinidad

      if (payload.correction.includes('Defined') || payload.correction.includes('Opened')) {
        throw new BadRequestError('Invalid correction settings: Cannot include "Defined" or "Opened" states.');
      }

      const nextCouponSettings = {
        gameAnalysis: payload.gameAnalysis,
        outcomeLabel: payload.outcomeLabel,
        tipsinfo: payload.tipsinfo,
        correction: payload.correction,
      } satisfies CouponSettings;

      if (payload.tipsinfoFull) {
        const definition = await definitionService.loadDefinitionAsync(product);
        // [CA] For now we rely on loadDefinitionAsync to throw if no definitions is found,
        // thereby we can set tipsinfo to be empty array to satisfy TypeScript.
        // This should however be taken care of with proper error handling when a strategy is set.
        let definitionTipsinfoOptions = definition?.tipsinfoOptions ?? [];
        if (settingsKey !== 'PLAY') {
          definitionTipsinfoOptions = definitionTipsinfoOptions.filter(
            (tipsinfo) => tipsinfo === 'Odds' || tipsinfo === 'SvenskaFolket' || tipsinfo === 'FavoriteOdds'
          );
        }
        nextCouponSettings.tipsinfo = definitionTipsinfoOptions.filter((tipsinfo) => tipsinfo !== 'Analysis');
      }

      return couponSettingsRepository.updateCouponSettingsAsync(
        product,
        nextCouponSettings satisfies CouponSettings,
        settingsKey
      );
    },
  };
}
